import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Search, LayoutDashboard, AlertTriangle, Users, ArrowRightLeft, History, Car, X, Package, MapPin, Settings, Route, Truck, Navigation } from 'lucide-react';
import { useAuth } from '@/hooks';
import { ROUTES } from '@/utils/constants';
import { cn } from '@/utils/cn';
import { getCars } from '@/api/cars';
import { Car as CarType } from '@/types/car';
import { useDebounce } from '@/hooks/useDebounce';

export const Sidebar: React.FC = () => {
  const { user } = useAuth();
  const [carSearch, setCarSearch] = useState('');
  const [searchResults, setSearchResults] = useState<CarType[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debouncedSearch = useDebounce(carSearch, 200);

  // Search for cars when debounced search changes
  useEffect(() => {
    const searchCars = async () => {
      if (!debouncedSearch || debouncedSearch.length < 1) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      try {
        setIsSearching(true);
        const response = await getCars({
          search: debouncedSearch,
          limit: 10,
          page: 1
        });
        setSearchResults(response.data.cars);
        setShowResults(true);
      } catch (error) {
        console.error('Error searching cars:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    searchCars();
  }, [debouncedSearch]);

  const clearSearch = () => {
    setCarSearch('');
    setSearchResults([]);
    setShowResults(false);
  };

  const navItems = [
    {
      to: ROUTES.SEARCH,
      icon: Search,
      label: 'البحث عن البطاريات',
      roles: ['call_center', 'accountant', 'manager'],
    },
    {
      to: ROUTES.DASHBOARD,
      icon: LayoutDashboard,
      label: 'لوحة التحكم',
      roles: ['accountant', 'manager'],
    },
    {
      to: ROUTES.CARS,
      icon: Car,
      label: 'دليل السيارات',
      roles: ['call_center', 'accountant', 'manager'],
    },
    {
      to: ROUTES.SERVICES,
      icon: Package,
      label: 'الخدمات المتاحة',
      roles: ['call_center', 'accountant', 'manager'],
    },
    {
      to: ROUTES.WINCH_PRICING,
      icon: Truck,
      label: 'أسعار الونش',
      roles: ['call_center', 'accountant', 'manager'],
    },
    {
      to: ROUTES.WINCH_START_POINTS,
      icon: Navigation,
      label: 'نقاط بداية الونش',
      roles: ['manager'],
    },
    {
      to: ROUTES.TRANSFER,
      icon: ArrowRightLeft,
      label: 'نقل بطاريات',
      roles: ['manager'],
    },
    {
      to: ROUTES.TRANSACTIONS,
      icon: History,
      label: 'سجل المعاملات',
      roles: ['accountant', 'manager'],
    },
    {
      to: ROUTES.LOW_STOCK,
      icon: AlertTriangle,
      label: 'المخزون المنخفض',
      roles: ['accountant', 'manager'],
    },
    {
      to: ROUTES.USERS,
      icon: Users,
      label: 'إدارة المستخدمين',
      roles: ['manager'],
    },
    {
      to: ROUTES.REGIONS,
      icon: MapPin,
      label: 'إدارة المناطق',
      roles: ['manager'],
    },
    {
      to: ROUTES.SERVICE_MANAGEMENT,
      icon: Settings,
      label: 'إدارة الخدمات',
      roles: ['manager'],
    },
    {
      to: ROUTES.ROUTE_MANAGEMENT,
      icon: Route,
      label: 'إدارة المسارات',
      roles: ['manager'],
    },
  ];

  const filteredNavItems = navItems.filter(item => 
    user && item.roles.includes(user.role)
  );

  return (
    <aside className="w-64 bg-dark-lighter border-l border-gray-700 min-h-[calc(100vh-73px)] flex flex-col">
      <nav className="p-4 space-y-2">
        {filteredNavItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg transition-all',
                  isActive
                    ? 'bg-primary text-dark font-semibold shadow-glow'
                    : 'text-gray-300 hover:bg-dark hover:shadow-glow-sm hover:text-primary'
                )
              }
            >
              <Icon className="h-5 w-5" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Quick Car Search Widget */}
      {user && ['call_center', 'accountant', 'manager'].includes(user.role) && (
        <div className="mt-auto p-4 border-t border-gray-700">
          <div className="mb-2">
            <h3 className="text-xs font-semibold text-gray-400 uppercase mb-2">بحث سريع عن السيارات</h3>
            <div className="relative">
              <input
                type="text"
                value={carSearch}
                onChange={(e) => setCarSearch(e.target.value)}
                placeholder="ماركة، موديل، أو سنة..."
                className="w-full bg-dark border border-gray-700 rounded-lg px-3 py-2 pr-8 text-sm text-gray-200 placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                autoComplete="off"
              />
              <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {carSearch && (
                  <button
                    onClick={clearSearch}
                    className="text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                <Search className="h-4 w-4 text-gray-500" />
              </div>
            </div>
          </div>

          {/* Search Results */}
          {showResults && (
            <div className="mt-2 max-h-80 overflow-y-auto bg-dark rounded-lg border border-gray-700 shadow-lg">
              {isSearching ? (
                <div className="p-3 text-center text-xs text-gray-400 animate-pulse">
                  <div className="inline-block">
                    <Search className="h-4 w-4 animate-spin" />
                  </div>
                  <div className="mt-1">جاري البحث...</div>
                </div>
              ) : searchResults.length > 0 ? (
                <div className="divide-y divide-gray-700">
                  {searchResults.map((car) => (
                    <div key={car.id} className="p-2.5 hover:bg-dark-lighter transition-all duration-150 cursor-pointer">
                      <div className="text-xs space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="font-bold text-primary text-sm">{car.make}</div>
                          <div className="text-gray-400 text-[10px] bg-gray-800 px-1.5 py-0.5 rounded">
                            {car.year}
                          </div>
                        </div>
                        <div className="text-gray-200 font-medium">{car.model}</div>
                        <div className="pt-1">
                          <div className="text-[9px] text-gray-500 mb-1">البطاريات المتوافقة:</div>
                          <div className="flex flex-wrap gap-1">
                            {car.batteries.map((battery) => (
                              <span
                                key={battery.id}
                                className="inline-flex items-center px-2 py-0.5 bg-primary/20 text-primary rounded text-[10px] font-semibold border border-primary/30"
                              >
                                {battery.batterySize}
                                {battery.count > 1 && (
                                  <span className="ml-1 text-[9px] opacity-75 bg-primary/30 px-1 rounded">
                                    ×{battery.count}
                                  </span>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-xs text-gray-400">
                  <div className="text-2xl mb-2">🔍</div>
                  <div>لا توجد نتائج مطابقة</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </aside>
  );
};
